/**
 * EventRow — one row in the events table with an inline expand toggle that
 * reveals the detail panel below the row.
 *
 * Parallel-expand grenzwert (per #327 edge-case 3): the expanded set lives in
 * the parent (EventsTable / EventsPage) so multiple rows can stay open at
 * once. This component is stateless w.r.t. the expanded flag.
 */

import React from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  TableCell,
  TableRow,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { formatDateSafe } from '../../utils/dateFormat';
import type { CreateIssueResponse, PortalEvent } from '../../services/types';
import EventDetailPanel from './EventDetailPanel';
import EventIssueCell from './EventIssueCell';

// NC-646: deutsche Labels + MUI-Color je Feedback-Kategorie (Bug/Feature/UX/Sonstiges).
const FEEDBACK_CATEGORY_LABEL = {
  bug: 'Bug',
  feature: 'Feature',
  ux: 'UX',
  other: 'Sonstiges',
} as const;

const FEEDBACK_CATEGORY_COLOR = {
  bug: 'error',
  feature: 'success',
  ux: 'info',
  other: 'default',
} as const;

// NC-645 part 1: deutsche Labels + MUI-Color je FeedbackStatus.
const FEEDBACK_STATUS_LABEL = {
  new: 'Neu',
  reviewed: 'In Arbeit',
  resolved: 'Erledigt',
  wontfix: 'Abgelehnt',
} as const;

const FEEDBACK_STATUS_COLOR = {
  new: 'default',
  reviewed: 'info',
  resolved: 'success',
  wontfix: 'warning',
} as const;

interface Props {
  event: PortalEvent;
  expanded: boolean;
  onToggle: () => void;
  /** Forwarded to EventIssueCell so the parent table can re-render the row
   *  with the freshly-linked issue (NC-637 / NC-638). */
  onIssueCreated?: (eventId: string, response: CreateIssueResponse) => void;
}

const EventRow: React.FC<Props> = ({ event, expanded, onToggle, onIssueCreated }) => {
  return (
    <>
      <TableRow hover>
        <TableCell sx={{ width: 48 }}>
          <IconButton
            size="small"
            onClick={onToggle}
            aria-label={expanded ? 'Detail einklappen' : 'Detail ausklappen'}
            aria-expanded={expanded}
          >
            {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          {formatDateSafe(event.createdAt, 'dd.MM.yyyy HH:mm')}
        </TableCell>
        <TableCell>
          <Chip
            label={event.type === 'feedback' ? 'Feedback' : 'Fehler'}
            color={event.type === 'feedback' ? 'info' : 'error'}
            size="small"
          />
        </TableCell>
        <TableCell>
          {event.type === 'feedback' ? (
            <Chip
              label={FEEDBACK_CATEGORY_LABEL[event.category]}
              color={FEEDBACK_CATEGORY_COLOR[event.category]}
              size="small"
              variant="outlined"
            />
          ) : (
            <span aria-label="Keine Kategorie">—</span>
          )}
        </TableCell>
        <TableCell>
          {event.type === 'feedback' ? (
            <Chip
              label={FEEDBACK_STATUS_LABEL[event.status]}
              color={FEEDBACK_STATUS_COLOR[event.status]}
              size="small"
            />
          ) : (
            <span aria-label="Kein Status">—</span>
          )}
        </TableCell>
        <TableCell>{event.summary}</TableCell>
        <TableCell>{event.userEmail}</TableCell>
        <TableCell sx={{ width: 110 }}>
          <EventIssueCell event={event} onIssueCreated={onIssueCreated} />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box>
              <EventDetailPanel event={event} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default EventRow;
